import { LocalCache } from 'apollo-client/queries/LocalCache';
import { pipe } from 'fp-ts/function';
import * as M from 'pattern-matching-ts/match';

import {
  DistributionGraphsInput,
  ExportCompetitorsAnalysisRequest,
  ExportMyStockRequest,
  GetCompetitorsAnalysisRequest,
  GetMarketTrendRequest,
  GetMyStockRequest,
  ListingFiltersInput,
  SaveSearchFiltersGroupInput,
} from 'generated/graphql';

interface CompetitorAnalysisAction {
  readonly payload: LocalCache;
  readonly type: 'COMPETITOR_ANALYSIS';
  readonly returnType: ExportCompetitorsAnalysisRequest | GetCompetitorsAnalysisRequest | GetMarketTrendRequest;
}

interface MyStockAction {
  readonly payload: LocalCache;
  readonly type: 'MY_STOCK';
  readonly returnType: ExportMyStockRequest | GetMyStockRequest;
}

interface GetMarketTrendAction {
  readonly payload: LocalCache;
  readonly type: 'GET_MARKET_TREND';
  readonly returnType: GetMarketTrendRequest;
}

interface MyStockDistributionAction {
  readonly payload: LocalCache;
  readonly type: 'MY_STOCK_DISTRIBUTION_GRAPHS';
  readonly returnType: { filters: DistributionGraphsInput };
}

interface CompetitorAnalysisDistributionAction {
  readonly payload: LocalCache;
  readonly type: 'COMPETITOR_ANALYSIS_DISTRIBUTION_GRAPHS';
  readonly returnType: { filters: DistributionGraphsInput };
}

interface GetMarketTrendDistributionAction {
  readonly payload: LocalCache;
  readonly type: 'GET_MARKET_TREND_DISTRIBUTION_GRAPHS';
  readonly returnType: { filters: DistributionGraphsInput };
}

interface SaveSearchFiltersGroupAction {
  readonly payload: ListingFiltersInput;
  readonly type: 'SAVE_SEARCH_FILTERS_GROUP';
  readonly returnType: ListingFiltersInput;
}

type Actions =
  | CompetitorAnalysisAction
  | GetMarketTrendAction
  | MyStockAction
  | SaveSearchFiltersGroupAction
  | MyStockDistributionAction
  | GetMarketTrendDistributionAction
  | CompetitorAnalysisDistributionAction;

const getYear = (maybeYear: string): number =>
  Number(maybeYear.length === 4 ? maybeYear : new Date(maybeYear).getFullYear());
const getLastDayOfTheYear = (maybeYear: string): Date => new Date(getYear(maybeYear), 11, 31);
const getFirstDayOfTheYear = (maybeYear: string): Date => new Date(getYear(maybeYear), 0, 1);

export const getPayloadFromFilters = <A extends LocalCache, R extends Actions['returnType']>(action: {
  data?: A;
  type: Actions['type'];
}): R => {
  const { data } = action;
  const sharedFilters: Actions['returnType'] = {
    ...{ modelNames: data?.modelNames?.length ? data?.modelNames : undefined },
    ...{ optionals: data?.optionals?.length ? data?.optionals : undefined },
    ...{ brandNames: data?.brandNames?.length ? data?.brandNames : undefined },
    ...{ powerSupplies: data?.powerSupplies?.length ? data?.powerSupplies : undefined },
    ...{ gearTypes: data?.gearTypes?.length ? data?.gearTypes : undefined },
    ...{ tractionTypes: data?.tractionTypes?.length ? data?.tractionTypes : undefined },
    ...{ vendorType: data?.vendorType?.length ? data?.vendorType : undefined },
    ...{ regions: data?.regions?.length ? data?.regions : undefined },
    ...{ provinces: data?.provinces?.length ? data?.provinces : undefined },
    ...{ isListingActive: data?.isListingActive === 'Active' ? true : false },
    ...{ country: data?.country },
    ...{ isPriceTiedToLoan: data?.isPriceTiedToLoan },
    ...{ vehicleTypes: data?.vehicleTypes },
    ...{ isVehicleCoveredByWarranty: data?.isVehicleCoveredByWarranty },
    ...{ isPictureAvailable: data?.isPictureAvailable },
    ...{ isVATDeductible: data?.isVATDeductible },
    ...{ interiorColors: data?.interiorColors?.length ? data?.interiorColors : undefined },
    ...{ coatingColors: data?.coatingColors?.length ? data?.coatingColors : undefined },
    ...{ priceLabels: data?.priceLabels?.length ? data?.priceLabels : undefined },
    ...{ powerInHP: data?.powerInHP?.min ? data?.powerInHP : undefined },
    ...{ priceInEUR: data?.priceInEUR?.min ? data?.priceInEUR : undefined },
    ...{ vehicleKM: data?.vehicleKM?.min ? data?.vehicleKM : undefined },
    ...{
      engineDisplacementInCM3: data?.engineDisplacementInCM3?.min ? data?.engineDisplacementInCM3 : undefined,
    },
    ...{
      vehicleRegistrationDateISO8601:
        data?.vehicleRegistrationDateISO8601?.min && data?.vehicleRegistrationDateISO8601?.max
          ? {
              max: getLastDayOfTheYear(data?.vehicleRegistrationDateISO8601?.max).toISOString(),
              min: getFirstDayOfTheYear(data?.vehicleRegistrationDateISO8601?.min).toISOString(),
            }
          : undefined,
    },
    ...{
      orderBy: data?.orderBy?.field
        ? {
            field: data.orderBy.field,
            sortDirection: data.orderBy.sortDirection,
          }
        : undefined,
    },
    ...{
      eurotaxCodes: data?.eurotaxCodes?.length ? data?.eurotaxCodes : undefined,
    },
    ...{
      modelVersions: data?.modelVersions?.length
        ? data?.modelVersions?.map((x) => (x as unknown as { label: string })?.label)
        : undefined,
    },
    ...{ websites: data?.websites?.length ? data?.websites : undefined },
    ...{
      publishDateFromISO8601: data?.publishDateFromISO8601 ?? undefined,
    },
    ...{
      publishDateToISO8601: data?.publishDateToISO8601 ?? undefined,
    },
  };

  return pipe(
    action,
    M.matchW('type')({
      GET_MARKET_TREND: (): GetMarketTrendRequest => sharedFilters,
      MY_STOCK: (): GetMyStockRequest => sharedFilters,
      SAVE_SEARCH_FILTERS_GROUP: (): SaveSearchFiltersGroupInput['filters'] => ({
        ...sharedFilters,
        vendorNames: action?.data?.vendorNames?.length ? action.data.vendorNames : [''],
      }),
      COMPETITOR_ANALYSIS: (): GetCompetitorsAnalysisRequest => ({
        ...sharedFilters,
        vendorNames: action?.data?.vendorNames?.length ? action.data.vendorNames : [''],
      }),
      MY_STOCK_DISTRIBUTION_GRAPHS: (): { filters: DistributionGraphsInput } => ({
        filters: {
          ...sharedFilters,
          //vendorName from userSettings
          vendorNames: action?.data?.vendorName ? [action.data.vendorName] : [''],
        },
      }),
      GET_MARKET_TREND_DISTRIBUTION_GRAPHS: (): { filters: DistributionGraphsInput } => ({
        filters: {
          ...sharedFilters,
        },
      }),
      COMPETITOR_ANALYSIS_DISTRIBUTION_GRAPHS: (): { filters: DistributionGraphsInput } => ({
        filters: {
          ...sharedFilters,
          vendorNames: action?.data?.vendorNames?.length ? action.data.vendorNames : [''],
        },
      }),
      _: (): GetMarketTrendRequest => sharedFilters,
    })
  ) as R;
};
